<template>
    <li>
        <div class="bg-white rounded flex items-stretch shadow py-20 px-24 gap-32 cursor-pointer">
            <a class="grow flex items-center gap-32" @click="goToJob">
                <div class="grow">
                    <div class="flex items-center mb-8">
                        <h1 class="text-body font-semibold group-hover:underline first-letter:capitalize">
                            {{ title }}
                        </h1>
                        <svg-icon
                            v-if="isSubscribed"
                            type="notifications-filled"
                            class="fill-semantic-info ml-4"
                        />
                        <booster-info v-if="job.booster" :booster="job.booster"/>
                    </div>
                    <div
                        v-if="!job.is_spontaneous"
                        class="flex items-center gap-12"
                    >
                        <div class="flex items-center gap-4">
                            <svg-icon type="location-on" class="w-18 h-18"></svg-icon>
                            <span>{{ jobLocation }}</span>
                        </div>
                        <div class="flex items-center gap-4">
                            <svg-icon type="watch" class="w-18 h-18"></svg-icon>
                            <span>{{ jobTypes }}</span>
                        </div>
                        <div
                            v-if="job.responsible_user"
                            class="flex items-center gap-4"
                        >
                            <svg-icon type="person" class="w-18 h-18"></svg-icon>
                            <span>{{ job.responsible_user.name }}</span>
                        </div>
                    </div>
                    <div class="flex items-center" :class="[ job.is_spontaneous  ? 'mt-8' : 'mt-20' ]">
                        <tag
                            :display="job.is_published ? 'success' : 'danger'"
                            no-icon
                        >
                            <span class="first-letter:uppercase">{{ job.is_published ? $t('jobs.published') : $t('jobs.unpublished') }}</span>
                        </tag>
                        <span class="text-primary-80 ml-4" v-if="job.schedule">
                            {{ autoPublishMessage }}
                        </span>
                    </div>
                </div>
                <div
                    class="self-stretch grid"
                    :class="influxAssist ? 'grid-cols-3' : 'grid-cols-2'"
                >
                    <div class="flex items-center justify-center border-r border-primary-20">
                        <div class="flex flex-col items-center gap-4 text-primary mx-12">
                            <span class="text-big font-semibold">{{ applicationCount }}</span>
                            <span class="first-letter:uppercase">{{ $t('jobs.candidates') }}</span>
                        </div>
                    </div>
                    <div class="flex items-center justify-center border-r border-primary-20">
                        <div class="flex flex-col items-center gap-4 text-primary mx-12">
                            <span class="text-big font-semibold">{{ newApplicationCount }}</span>
                            <span class="first-letter:uppercase">{{ $t('general.new') }}</span>
                        </div>
                    </div>
                    <div
                        v-if="influxAssist"
                        class="flex items-center justify-center border-r border-primary-20"
                    >
                        <div class="flex flex-col items-center gap-4 text-primary mx-12">
                            <span class="text-big font-semibold">{{ $numAbbr(job.new_applications_count, 0) }}</span>
                            <span class="first-letter:uppercase">
                                {{ $t('applications.latest_influx', { count: latestApplicationsInflux }) }}
                            </span>
                        </div>
                    </div>
                </div>
            </a>
            <div class="flex flex-col gap-8">
                <simple-button
                    display="neutral"
                    look="icon"
                    icon="edit"
                    @click="edit"
                />

                <simple-dropdown
                    class="pointer-events-auto"
                    v-if="!job.is_spontaneous"
                    position="right"
                    without-chevron
                >
                    <template #trigger>
                        <simple-button
                            display="neutral"
                            look="icon"
                            icon="more-vert"
                        />
                    </template>
                    <template #content="{ close }">
                        <nav
                            aria-orientation="vertical"
                            class="bg-white relative rounded shadow"
                            role="menu"
                        >
                            <dropdown-item
                                icon="archive"
                                :input-label="job.archived ? $t('jobs.unarchive_job') : $t('jobs.archive_job')"
                                @click="close(); job.archived ? jobsModuleStore.startUnarchivingJob(job) : jobsModuleStore.startArchivingJob(job)"
                            />
                            <dropdown-item
                                icon="content-copy"
                                :input-label="$t('jobs.duplicate_job')"
                                @click="close(); duplicateJob(job, job.archived)"
                            />
                            <dropdown-item
                                danger
                                icon="delete"
                                :input-label="$t('jobs.delete_job')"
                                v-if="job.archived"
                                @click="close(); jobsModuleStore.confirmDelete(job)"
                            />
                        </nav>
                    </template>
                </simple-dropdown>

                <div class="relative">
                    <div
                        v-if="suggestBoosting"
                        class="absolute rounded-32 w-full h-full bg-semantic-error pulsating"
                    ></div>
                    <simple-button
                        :display="suggestBoosting ? 'danger' : 'neutral'"
                        look="icon"
                        :icon="suggestBoosting ? 'campaign' : 'share'"
                        @click="suggestBoosting ? selectingCampaignPackage = true : edit(true)"
                    />
                </div>
            </div>
        </div>

        <popup-modal
            v-if="selectingCampaignPackage"
            class="w-768"
            @close="selectingCampaignPackage = false"
        >
            <campaign-package-overview :job="job"/>
        </popup-modal>
    </li>

</template>

<script>
import RoundButton from './ui/RoundButton.vue';
import SimpleDropdown from './ui/SimpleDropdown.vue';
import SvgIcon from './ui/SvgIcon.vue';
import * as jobs from '../services/JobService';
import Job from '../models/Job';
import {normalizeJson, ucfirst} from '../../shared/helpers';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import eventbus from '../../shared/eventbus';
import BoosterInfo from "./BoosterInfo.vue";
import moment from "moment";
import SystemParameter from "../models/SystemParameter";
import PopupModal from "./ui/PopupModal.vue";
import CampaignPackageOverview from "./CampaignPackageOverview.vue";
import numberAbbreviation from "../plugins/numberAbbreviation";
import PaidSubscription from "../models/PaidSubscription";
import Tag from "./ui/Tag.vue";
import SimpleButton from "./ui/SimpleButton.vue";
import DropdownItem from "./ui/DropdownItem.vue";
import {mapStores} from "pinia";
import {useMainStore} from "../state/store";
import {useAuthenticationStore} from "../state/modules/authentication";
import {useRepo} from "pinia-orm";
import {useNotificationsStore} from "../state/modules/notifications";
import {useJobsStore} from "../state/modules/jobs";

export default {
    components: {
        DropdownItem,
        SimpleButton,
        Tag,
        CampaignPackageOverview,
        PopupModal,
        'round-button': RoundButton,
        'simple-dropdown': SimpleDropdown,
        'svg-icon': SvgIcon,
        BoosterInfo
    },
    props: {
        job: Object,
        draggable: Boolean,
    },
    plugins: [numberAbbreviation],
    data() {
        return {
            selectingCampaignPackage: false,
        }
    },
    computed: {
        ...mapStores(useMainStore, useNotificationsStore, useJobsStore, useAuthenticationStore),
        locale() {
            return this.mainStore.locale;
        },
        datetimeFormat() {
            return this.mainStore.datetimeFormat;
        },
        contentLanguage() {
            return this.authenticationStore.contentLanguage;
        },
        currentUser() {
            return this.authenticationStore.currentUser;
        },
        currentCompany() {
            return this.mainStore.currentCompany;
        },
        influxAssist() {
            return this.currentCompany.influx_assist;
        },
        title() {
            if (this.job.is_spontaneous) {
                return this.$t('jobs.spontaneous_applications');
            }

            return ucfirst(this.translated(this.job.title, this.contentLanguage, true) || this.$t('jobs.missing_title'));
        },
        newApplicationCount() {
            let firstStage = this.job.stages.find(stage => stage.order == 0);

            return firstStage ? this.$numAbbr(firstStage.applications_count - firstStage.rejected_count, 0) : 0;
        },
        applicationCount() {
            return this.$numAbbr(this.job.stages.reduce((carry, stage) => carry + (
                //Extra check if a candidate is in the onboarding stage, then don't count him as active candidate
                (!stage.onboarding ? stage.applications_count - stage.rejected_count : 0)
            ), 0), 0);
        },
        totalApplications() {
            return this.job.stages.reduce((carry, stage) => carry + stage.applications_count, 0);
        },
        autoPublishMessage() {
            if (!this.job.schedule) {
                return '';
            }

            if (this.job.is_published && this.job.end_date !== null && this.job.endMoment.isAfter()) {
                return this.$t('jobs.auto_depublish', {
                    date: this.job.endMoment.locale(this.locale).format(this.datetimeFormat)
                });
            }

            if (!this.job.is_published && this.job.start_date !== null && this.job.startMoment.isAfter()) {
                return this.$t('jobs.auto_publish', {
                    date: this.job.startMoment.locale(this.locale).format(this.datetimeFormat)
                });
            }
        },
        hasMultiPostingSubscription() {
            return !!this.multiPostingSubscription;
        },
        multiPostingSubscription() {
            return useRepo(PaidSubscription)
                .where('type', 'multi-posting')
                .where('company_id', this.currentCompany.id)
                .where('active', true)
                .first();
        },
        jobTypes () {
            let jobTypes = [];

            this.job.jobTypes.forEach(jobType => {
                jobTypes.push(this.$t('job_parameters.type.' + jobType));
            });

            return jobTypes.join(', ');
        },
        isSubscribed () {
            return this.job.subscriptions.some(subscription => {
                return subscription.user_id === this.currentUser.id;
            });
        },
        can () {
            return {
                view: this.$can(['jobs.view', 'jobs.view.' + this.job.id]),
                manage: this.$can(['jobs.manage', 'jobs.manage.' + this.job.id]),
                manageStrict: this.$can('jobs.manage')
            };
        },
        jobLocation() {
            let location = this.job.city;
            if (this.job.location)
                location = this.job.location.city;

            return location || "---";
        },
        boosterInfo() {
            const booster = this.job.booster;
            const published = moment(booster.published_at).locale(this.locale).format(this.datetimeFormat);
            const expires = moment(booster.expires_at).locale(this.locale).format(this.datetimeFormat);

            return `${booster.title[this.locale]}: ${published} - ${expires}`;
        },
        newApplicationsThreshold() {
            return useRepo(SystemParameter).where('key', 'new_applications_influx_threshold').first()?.value;
        },
        latestApplicationsInflux() {
            return useRepo(SystemParameter).where('key', 'new_applications_influx_latest').first()?.value;
        },
        thresholdExceeded() {
            return this.applicationCount >= this.newApplicationsThreshold;
        },
        influxDateExceeded() {
            let newApplicationsInfluxDays = useRepo(SystemParameter)
                .where('key', 'new_applications_influx_days')
                .first()
                ?.value;
            return moment(this.job.last_published_at).isBefore(moment().subtract(newApplicationsInfluxDays, 'd'));
        },
        suggestBoosting() {
            if (!this.influxAssist) return false;
            if (this.hasMultiPostingSubscription) return false;

            return this.influxDateExceeded && !this.thresholdExceeded && !this.job.is_spontaneous && this.job.is_published &&
                this.job.active_campaigns_count === 0;
        },
        newApplicationsTextColor() {
            if (!this.job.is_published || this.job.is_spontaneous || !this.influxAssist) {
                return 'text-primary-80';
            } else if (!this.influxDateExceeded && !this.applicationCount) {
                return 'text-primary-20';
            } else if (!this.influxDateExceeded) {
                return 'text-primary-20';
            } else if (this.influxDateExceeded && !this.thresholdExceeded) {
                return 'text-primary-80';
            } else {
                return 'text-secondary';
            }
        }
    },
    methods: {
        goToJob () {
            this.$router.push({ name: 'job', params: { id: this.job.id } });
        },
        edit(share = false) {
            this.$router.push({
                name: 'jobs.edit',
                params: {
                    id: this.job.id
                },
                query: {
                    from: 'jobs',
                    step: share ? 5 : 1
                }
            })
        },
        duplicateJob(job, archived = false) {
            jobs.duplicate(job.id, {
                data: {
                    archived: archived
                }
            })
                .then(result => {
                    let resultData = normalizeJson(result);

                    let jobs = useRepo(Job)
                        .where('company_id', job.company_id)
                        .where('sequence', value => value >= resultData.sequence)
                        .get()
                        .map(entry => ({
                            id: entry.id,
                            sequence: entry.sequence++
                        }));

                    useRepo(Job).save(jobs);

                    return useRepo(Job).save(resultData);
                }).then(result => {
                eventbus.dispatch('fetch-job-limits');
                this.addJobPermissions(result)

                this.notificationsModuleStore[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('jobs.duplicate_success', {name: result.title[this.locale]}),
                });
            })
        },
        addJobPermissions (job) {
            let permissions = this.authenticationStore.permissions;

            if (!permissions[this.currentCompany.id])
                permissions[this.currentCompany.id] = [];

            if (!permissions[this.currentCompany.id].includes('jobs.view.' + job.id)) {
                permissions[this.currentCompany.id].push('jobs.view.' + job.id);
                permissions[this.currentCompany.id].push('jobs.manage.' + job.id);

                permissions[this.currentCompany.id].push('job-applications.view.' + job.id);
                permissions[this.currentCompany.id].push('job-applications.view.' + job.id);
            }

            this.authenticationStore.setPermissions(permissions);
        },
    },
}
</script>

<style scoped>
.pulsating {
    transform-origin: center;
    animation: pulsate 1.5s infinite;
}

@keyframes pulsate {
    0% {
        opacity: 1;
    }
    100% {
        opacity: 0;
        transform: scale(1.25);
    }
}
</style>
